// src/App.jsx

import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { useEffect, useRef } from "react";
import { HelmetProvider } from "@dr.pogodin/react-helmet";
import { LoadScript } from "@react-google-maps/api";
import ScrollToTopOnRoute from "components/ScrollToTopOnRoute/ScrollToTopOnRoute";
import BandProfile from "./components/Esiintyjaprofiili/Esiintyjaprofiili";
import Home from "./Pages/Etusivu/Etusivu";
import Paavalikko from "./components/Paavalikko/Paavalikko";
import Footer from "./components/Footer/Footer";
import { ThemeProvider } from "./context/ThemeContext";
import TietoaMeista from "./Pages/Tietomeista/TietoaMeista";
import UKK from "./Pages/UKK/UKK.jsx";
import OtaYhteytta from "./Pages/OtaYhteytta/OtaYhteytta.jsx";
import ScrollToTop from "./components/utils/ScrollToTop";
import CookieConsent from "./components/Cookies/CookieConsent";
import Tietosuoja from "./Pages/Tietosuoja/Tietosuoja.jsx";
import UKKArtists from "./Pages/UKK/UKKArtists.jsx";
import PerformersPage from "./Pages/Performers/PerformersPage";
import Kayttoehdot from "./Pages/Kayttoehdot/Kayttoehdot.jsx";
import Blogi from "./Pages/Blogi/Blogi.jsx";

import YhteistyoPage from "./Pages/Yhteistyo/Yhteistyo.jsx";
import NotFound from "./Pages/NotFound/NotFound.jsx";

import DjLanding from "./Pages/Landing/DjLanding.jsx";
import BilebandiLanding from "./Pages/Landing/BilebandiLanding.jsx";
import TrubaduuriLanding from "./Pages/Landing/TrubaduuriLanding.jsx";

// Aluekohtaiset (valmis: Uusimaa)
import BilebandiUusimaaLanding from "./Pages/Landing/BilebandiUusimaaLanding.jsx";
import TrubaduuriUusimaaLanding from "./Pages/Landing/TrubaduuriUusimaaLanding.jsx";
import DjUusimaaLanding from "./Pages/Landing/DjUusimaaLanding.jsx";

// Aluekohtaiset: Pirkanmaa
import BilebandiPirkanmaaLanding from "./Pages/Landing/BilebandiPirkanmaaLanding.jsx";
import TrubaduuriPirkanmaaLanding from "./Pages/Landing/TrubaduuriPirkanmaaLanding.jsx";
import DjPirkanmaaLanding from "./Pages/Landing/DjPirkanmaaLanding.jsx";

// Aluekohtaiset: Päijät-Häme
import BilebandiPaijatHameLanding from "./Pages/Landing/BilebandiPaijatHameLanding.jsx";
import TrubaduuriPaijatHameLanding from "./Pages/Landing/TrubaduuriPaijatHameLanding.jsx";
import DjPaijatHameLanding from "./Pages/Landing/DjPaijatHameLanding.jsx";

// Aluekohtaiset: Varsinais-Suomi
import BilebandiVarsinaisSuomiLanding from "./Pages/Landing/BilebandiVarsinaisSuomiLanding.jsx";
import TrubaduuriVarsinaisSuomiLanding from "./Pages/Landing/TrubaduuriVarsinaisSuomiLanding.jsx";
import DjVarsinaisSuomiLanding from "./Pages/Landing/DjVarsinaisSuomiLanding.jsx";

import SingleBlog from "./Pages/SingleBlog/SingleBlog.jsx";
import BookingResult from "./Pages/BookingResult/BookingResult.jsx";
import "aos/dist/aos.css";
import emailjs from "emailjs-com";
import AppToast from "./ui/AppToast";
import "react-toastify/dist/ReactToastify.css";
import TeamNoteModal from "./components/TeamNoteModal/TeamNoteModal";
import CompanyProfile from "./components/Yritysprofiili/CompanyProfile.jsx";
import Register from "./Pages/Auth/Register.jsx";
import Login from "./Pages/Auth/Login.jsx";
import OnboardingGate from "./Pages/Onboarding/OnboardingGate.jsx";
import Forgot from "./Pages/Auth/Forgot.jsx";
import Reset from "./Pages/Auth/Reset.jsx";
import VerifyEmail from "./Pages/Auth/VerifyEmail.jsx";
import OAuthFinish from "./Pages/Auth/OAuthFinish.jsx";
import ProfileReviewResult from "./Pages/ProfileReviewResult/ProfileReviewResult";
import SetupWizardAdd from "./Pages/Setup/SetupWizardAdd.jsx";
import Start from "./Pages/Start/Start.jsx";

// Auth
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/auth/ProtectedRoute.jsx";
import Dashboard from "./Pages/Dashboard/Dashboard.jsx";
import FloatingDashboardCTA from "./components/FloatingDashboardCTA/FloatingDashboardCTA.jsx";
import { trackPageView } from "./utils/analytics";

if (process.env.NODE_ENV === "development") {
  window.emailjs = emailjs;
  emailjs.init(process.env.REACT_APP_EMAILJS_USER_ID?.trim());
}

const MAPS_DEV_FAKE =
  String(process.env.REACT_APP_MAPS_DEV_FAKE || "").toLowerCase() === "true";
const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// CRA + Vite -yhteensopivat env-liput
const IS_PROD =
  ((typeof process !== "undefined" && process.env && process.env.NODE_ENV === "production") ||
   (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.PROD === true));
const IS_DEV = !IS_PROD;

// --- Meta Pixel: lähetä PageView jokaisessa reitinvaihdossa (SPA)
function FbqPageView() {
  const loc = useLocation();
  const first = useRef(true);
  useEffect(() => {
    // Vältä tupla: base-pikseli lähetti jo ensimmäisen PageView'n
    if (first.current) { first.current = false; return; }
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "PageView");
    }
  }, [loc.pathname, loc.search]);
  return null;
}

// --- GA4: manuaalinen page_view jokaisesta reitinvaihdosta
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    // Käytetään virtuaalisena polkuna pathname + search (ei hashia)
    const path = location.pathname + location.search;
    const title =
      typeof document !== "undefined" ? document.title : undefined;

    try {
      trackPageView(path, title);
    } catch (err) {
      if (IS_DEV) {
        console.warn("trackPageView failed", err);
      }
    }
  }, [location.pathname, location.search]);

  return null;
}

 // Alias-komponentti: /login -> /kirjaudu (säilyttää ?queryn)
 function LoginAlias() {
   const location = useLocation();
   return <Navigate to={`/kirjaudu${location.search || ""}`} replace />;
}

// Alias: /preview/esiintyja/:slug -> /esiintyja/:slug?preview=1&...
function PreviewAlias() {
  const { slug } = useParams();
  const loc = useLocation();
  const qs = new URLSearchParams(loc.search || "");
  qs.set("preview", "1");
  return <Navigate to={`/esiintyja/${slug}?${qs.toString()}`} replace />;
}

// Yleiskäyttöinen alias, joka säilyttää queryt
function RedirectPreserveQuery({ to }) {
  const loc = useLocation();
  return <Navigate to={`${to}${loc.search || ""}`} replace />;
}

// Kevyt apu: ohjaa suoraan hakunäkymään esivalituilla parametreilla
function RegionRedirect({ type, region, book = false }) {
  const qs = new URLSearchParams();
  qs.set("type", String(type));
  qs.set("maakunta", String(region));
  if (book) qs.set("book", "1");
  qs.set("clear", "1");
  return <Navigate to={`/?${qs.toString()}#tulokset`} replace />;
}


function AppShell() {
  return (
      <div className="min-h-screen overflow-x-hidden relative bg-neon-gradient dark:bg-bile-dark">
      {/*
        Talleta esikatselutoken kerran kun sovellus käynnistyy (ja kun käyttäjä
        saapuu esikatselulinkistä). Ei vaikuta muihin reitteihin.
      */}


      <div className="relative z-10">
        <BrowserRouter>
          <ScrollToTopOnRoute />
          <ScrollToTop />
          <FbqPageView />
          <AnalyticsTracker />
          {/* ★ AuthProvider siirretty ylös – kietoo myös Paavalikon */}
          <AuthProvider>
            <Paavalikko />

            <Routes>
              <Route path="/" element={<Home />} />

               {/* Landing - KOKO SUOMI */}
                <Route path="/dj-juhliin/*"        element={<DjLanding />} />
                <Route path="/bilebandi-juhliin/*" element={<BilebandiLanding />} />
                <Route path="/trubaduuri-juhliin/*" element={<TrubaduuriLanding />} />

                {/* ---- Maakuntaländärit: Uusimaa ---- */}
                <Route path="/bilebandi-uusimaa/*"   element={<BilebandiUusimaaLanding />} />
                 <Route path="/dj-uusimaa/*" element={<DjUusimaaLanding />} />
                <Route path="/trubaduuri-uusimaa/*" element={<TrubaduuriUusimaaLanding />} />

                  {/* ---- Pirkanmaa: omat React-ländärit ---- */}
                <Route path="/bilebandi-pirkanmaa/*"   element={<BilebandiPirkanmaaLanding />} />
                <Route path="/dj-pirkanmaa/*"          element={<DjPirkanmaaLanding />} />
                <Route path="/trubaduuri-pirkanmaa/*"  element={<TrubaduuriPirkanmaaLanding />} />

                {/* ---- Päijät-Häme: omat React-ländärit ---- */}
                <Route path="/bilebandi-paijat-hame/*" element={<BilebandiPaijatHameLanding />} />
                <Route path="/dj-paijat-hame/*"         element={<DjPaijatHameLanding />} />
                <Route path="/trubaduuri-paijat-hame/*" element={<TrubaduuriPaijatHameLanding />} />

                {/* ----  Varsinais-Suomi – toistaiseksi suora ohjaus hakuun ---- */}
                <Route path="/bilebandi-varsinais-suomi/*" element={<BilebandiVarsinaisSuomiLanding />}/>
                <Route path="/dj-varsinais-suomi/*" element={<DjVarsinaisSuomiLanding />} />
                <Route path="/trubaduuri-varsinais-suomi/*" element={<TrubaduuriVarsinaisSuomiLanding />}/>

                {/* Pohjois-Pohjanmaa – toistaiseksi suora ohjaus hakuun */}
                <Route path="/bilebandi-pohjois-pohjanmaa/*"   element={<RegionRedirect type="bilebandi" region="pohjois-pohjanmaa" />} />
                <Route path="/dj-pohjois-pohjanmaa/*"          element={<RegionRedirect type="dj"        region="pohjois-pohjanmaa" />} />
                <Route path="/trubaduuri-pohjois-pohjanmaa/*"  element={<RegionRedirect type="trubaduuri" region="pohjois-pohjanmaa" />} />

                {/* Keski-Suomi – toistaiseksi suora ohjaus hakuun */}
                <Route path="/bilebandi-keski-suomi/*"   element={<RegionRedirect type="bilebandi" region="keski-suomi" />} />
                <Route path="/dj-keski-suomi/*"          element={<RegionRedirect type="dj"        region="keski-suomi" />} />
                <Route path="/trubaduuri-keski-suomi/*"  element={<RegionRedirect type="trubaduuri" region="keski-suomi" />} />
              
                {IS_DEV && (
                  <Route path="/preview/esiintyja/:slug" element={<PreviewAlias />} />
                )}
              <Route path="*" element={<NotFound />} />
              
              <Route path="/esiintyja/:slug" element={<BandProfile />} />
              <Route path="/yritys/:slug" element={<CompanyProfile />} />
              <Route path="/blogi" element={<Blogi />} />
              <Route path="/blogi/:slug" element={<SingleBlog />} />
              <Route path="/tietoameista" element={<TietoaMeista />} />
              {/* Yhteistyö / sijoittajalle -sivu */}
              <Route path="/yhteistyo" element={<YhteistyoPage />} />
              <Route path="/useinkysyttya" element={<UKK />} />
              <Route path="/ukk-esiintyjille" element={<UKKArtists />} />
              <Route path="/otayhteytta" element={<OtaYhteytta />} />
              <Route path="/ukk" element={<Navigate to="/useinkysyttya" replace />} />
              <Route path="/backstage/*" element={<PerformersPage />} />
              <Route path="/kayttoehdot" element={<Kayttoehdot />} />
              <Route path="/tietosuoja" element={<Tietosuoja />} />

              {/* Varausresult: palvele molemmat muodot suoraan; wildcard hoitaa trailing slashin */}
              <Route path="/varaus/kiitos/*"  element={<BookingResult />} />
              <Route path="/varaus/peruttu/*" element={<BookingResult />} />
              <Route path="/kiitos/*"         element={<BookingResult />} />
              <Route path="/peruttu/*"        element={<BookingResult />} />

              {/* Roska-slugit → kanonisiin, säilytä queryt */}
              <Route path="/varaus__trashed/kiitos/*"
                     element={<RedirectPreserveQuery to="/varaus/kiitos" />} />
              <Route path="/varaus__trashed/peruttu/*"
                     element={<RedirectPreserveQuery to="/varaus/peruttu" />} />

              {/* (poistettu geneerinen /varaus/* -> BookingResult -fallback, ettei kaapata muita polkuja vahingossa) */}

              <Route path="/rekisteroidy" element={<Register />} />
              {/* alias: tue myös /login (esim. vanhoista linkeistä) */}
              <Route path="/login" element={<LoginAlias />} />
              <Route path="/kirjaudu" element={<Login />} />
              <Route path="/onboarding" element={<OnboardingGate />} />
              <Route path="/setup/add" element={<SetupWizardAdd />} />
              <Route path="/setup" element={<Navigate to="/dashboard?band=user" replace />} />
              <Route path="/setup/*" element={<Navigate to="/dashboard?band=user" replace />} />
              <Route path="/forgot" element={<Forgot />} />
              <Route path="/reset" element={<Reset />} />
              <Route path="/start" element={<Start />} />
              <Route path="/oauth/google/callback" element={<OAuthFinish provider="google" />} />
              <Route path="/profiili/hyvaksytty" element={<ProfileReviewResult />} />
              <Route path="/profiili/hylatty"    element={<ProfileReviewResult />} />
              <Route path="/profiili/virhe"      element={<ProfileReviewResult />} />

                {/* ✉️ Sähköpostivahvistus */}
              <Route path="/verify-email" element={<VerifyEmail />} />

              {/* Suojattu Dashboard */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                    
                  </ProtectedRoute>
                }
              />
            </Routes>

              {/* Leijuva dashboard-CTA vain kirjautuneelle */}
              <FloatingDashboardCTA />

            <Footer />
            <CookieConsent />
            {/* Globaali toast-kontti; teema määräytyy dark/light mukaan */}
            <AppToast />
            <TeamNoteModal />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        {/* Lataa Maps-skripti vain kun ei feikata ja avain on olemassa */}
        {!MAPS_DEV_FAKE && GMAPS_KEY ? (
          <LoadScript googleMapsApiKey={GMAPS_KEY} libraries={["places"]}>
            <AppShell />
          </LoadScript>
        ) : (
          <AppShell />
        )}
      </ThemeProvider>
    </HelmetProvider>
  );
}
